import type { TimedCinematicWaypoint } from '../../domain/cinematic-timeline';
import { cinematicPropertyTimeline, cinematicTrackTimeline } from '../../domain/cinematic-property-timeline';
import { CAMERA_TRACKS, type CameraKeyframes, type CameraTrack } from '../../domain/camera-keyframes';
import { filmTraversalBounds } from '../../domain/camera-plan';
import { readScenePropertyToken } from './scene-property-edit';
import { clamp } from './timeline-scale';
import type { TimelineDrag } from './use-timeline-gesture';

export interface TimelineKeyPreview { id: string; frame: number; kind?: 'key' | 'transition' }
export interface CameraKeyPreview extends TimelineKeyPreview { axis: CameraTrack }

export function keyframeDragTarget(waypoints: readonly TimedCinematicWaypoint[], tracks: CameraKeyframes | undefined, duration: number, drag: TimelineDrag) {
  if (drag.kind === 'attribute' || drag.kind === 'attribute-transition') {
    const separator = drag.id.indexOf(':'), axis = drag.id.slice(0, separator) as CameraTrack, id = drag.id.slice(separator + 1);
    if (!CAMERA_TRACKS.includes(axis)) return undefined;
    const keys = tracks?.[axis] ?? [], index = keys.findIndex((key, index) => (key.id ?? String(index)) === id), key = keys[index];
    return key ? { kind: 'camera' as const, axis, id, seconds: key.seconds, previous: keys[index - 1]?.seconds ?? 0,
      next: keys[index + 1]?.seconds ?? duration, arrival: true } : undefined;
  }
  if (drag.kind === 'waypoint' || drag.kind === 'scene-transition') {
    const { id, property } = readScenePropertyToken(drag.id);
    const keys = property ? cinematicPropertyTimeline(waypoints, property, duration) : waypoints;
    const index = keys.findIndex(item => item.waypoint.id === id), item = keys[index];
    return item ? { kind: 'scene' as const, id, property, seconds: item.seconds, previous: keys[index - 1]?.seconds ?? 0,
      next: keys[index + 1]?.seconds ?? (item.waypoint.filmProgress === undefined ? filmTraversalBounds(duration).endSeconds : duration),
      arrival: item.waypoint.transition.timing === 'arrival' } : undefined;
  }
  return undefined;
}

export function constrainKeyframeDrag(waypoints: readonly TimedCinematicWaypoint[], tracks: CameraKeyframes | undefined, duration: number, drag: TimelineDrag) {
  if (drag.kind !== 'scene-transition' && drag.kind !== 'attribute-transition') return drag.frame;
  const key = keyframeDragTarget(waypoints, tracks, duration, drag);
  if (!key) return drag.frame;
  const last = duration * 30 - 1, scale = last / duration, frame = Math.round(key.seconds * scale);
  const minimum = Math.ceil(.1 * scale), maximum = Math.floor(30 * scale);
  if (key.arrival) {
    const first = Math.max(0, Math.ceil(key.previous * scale - 1e-9), frame - maximum);
    return Math.round(clamp(drag.frame, first, Math.max(first, frame - minimum)));
  }
  const end = Math.min(last, Math.floor(key.next * scale + 1e-9), frame + maximum);
  return Math.round(clamp(drag.frame, Math.min(end, frame + minimum), end));
}

export function draggedTransitionSeconds(key: { seconds: number; arrival: boolean }, frame: number, duration: number) {
  const seconds = frame / (duration * 30 - 1) * duration;
  return clamp(key.arrival ? key.seconds - seconds : seconds - key.seconds, .1, 30);
}

export function previewSceneTrack(waypoints: TimedCinematicWaypoint[], duration: number, preview?: TimelineKeyPreview) {
  if (!preview) return waypoints;
  const last = duration * 30 - 1;
  return cinematicTrackTimeline(waypoints.map(item => {
    if (item.waypoint.id !== preview.id) return item;
    if (preview.kind === 'transition') return { ...item, waypoint: { ...item.waypoint, transition: {
      ...item.waypoint.transition, type: 'smooth' as const, entireInterval: false,
      durationSeconds: draggedTransitionSeconds({ seconds: item.seconds, arrival: item.waypoint.transition.timing === 'arrival' }, preview.frame, duration),
    } } };
    return { ...item, seconds: preview.frame / last * duration, waypoint: { ...item.waypoint, filmProgress: preview.frame / last } };
  }), duration);
}

export function previewCameraTrack(tracks: CameraKeyframes | undefined, axis: CameraTrack, duration: number, preview?: CameraKeyPreview) {
  return (tracks?.[axis] ?? []).map((key, index) => {
    const id = key.id ?? String(index), current = { ...key, id };
    if (preview?.axis !== axis || preview.id !== id) return current;
    return preview.kind === 'transition' ? { ...current, durationSeconds: draggedTransitionSeconds({ seconds: key.seconds, arrival: true }, preview.frame, duration) }
      : { ...current, seconds: preview.frame / (duration * 30 - 1) * duration };
  }).sort((a, b) => a.seconds - b.seconds);
}
